import { lazy } from 'react';

/**
 * LEAF_PAGE_ROUTES — single source of truth for "leaf" routes:
 *   • Simple single-Component pages
 *   • No <AdvancedSchemas /> / <AEOContent /> / <LEOContent /> wrappers
 *   • No dynamic `:slug` params
 *   • No <Navigate> redirects
 *
 * Routes that need wrappers / dynamic params / redirects stay inline in App.js
 * (they have unique element shapes that don't compress into a uniform map).
 *
 * Order is preserved from App.js so that any duplicate-path declarations
 * resolve identically (React Router's first-match-wins behavior).
 *
 * Chunking strategy:
 *   • Sprint 1 Myntra spokes share the "sprint1-services" chunk
 *   • Most other pages are individual lazy imports (already in use)
 */

// ─── Sprint 1 Myntra Spokes (shared chunk) ─────────────────────────────
// MyntraAccountManagementPage removed — /myntra-seller-account-management
// is now a Sprint 4 flagship rebuild mounted in App.js
const MarketplaceAccountManagementHubPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MarketplaceAccountManagementHubPage }))
);
// Phase X.10 — educational replacement for the former Amazon service-cluster page
const AmazonAccountManagementGuide = lazy(() =>
  import(/* webpackChunkName: "amazon-edu" */ "../pages/AmazonAccountManagementGuide")
);
const MyntraCatalogingServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraCatalogingServicesPage }))
);
const MyntraPhotoshootServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraPhotoshootServicesPage }))
);
const MyntraPLAAdvertisingServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraPLAAdvertisingServicesPage }))
);
const MyntraReturnManagementServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraReturnManagementServicesPage }))
);
const MyntraPaymentReconciliationServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraPaymentReconciliationServicesPage }))
);
const MyntraListingOptimizationServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraListingOptimizationServicesPage }))
);
const MyntraSalesGrowthServicesPage = lazy(() =>
  import(/* webpackChunkName: "sprint1-services" */ "../pages/Sprint1ServicePages").then(m => ({ default: m.MyntraSalesGrowthServicesPage }))
);

// ─── Onboarding Guides ────────────────────────────────────────────────
const NykaaSellerOnboardingPage = lazy(() => import("../pages/NykaaSellerOnboardingPage"));
const AjioSellerOnboardingPage = lazy(() => import("../pages/AjioSellerOnboardingPage"));
const TiraSellerOnboardingPage = lazy(() => import("../pages/TiraSellerOnboardingPage"));
const PerniasPopUpShopPage = lazy(() => import("../pages/PerniasPopUpShopPage"));

// ─── Misc landing & founder pages ─────────────────────────────────────
const JiniStudioPage = lazy(() => import("../pages/JiniStudioPage"));
const DeepinderAhluwaliaPage = lazy(() => import("../pages/DeepinderAhluwaliaPage"));
const MyntraOnboardingCompanyIndia = lazy(() => import("../pages/MyntraOnboardingCompanyIndia"));
const MyntraLandingPage = lazy(() => import("../pages/MyntraLandingPage"));
const NykaaLandingPage = lazy(() => import("../pages/NykaaLandingPage"));
const AjioLandingPage = lazy(() => import("../pages/AjioLandingPage"));
const PremiumMarketplaceAccessReport = lazy(() => import("../pages/PremiumMarketplaceAccessReport"));
const PetMarketplaceOnboarding = lazy(() => import("../pages/PetMarketplaceOnboarding"));

// ─── LLM/GEO Pillar Pages ─────────────────────────────────────────────
const MyntraSellerOnboardingPillar = lazy(() => import("../pages/MyntraSellerOnboardingPillar"));
const NykaaSellerOnboardingPillar = lazy(() => import("../pages/NykaaSellerOnboardingPillar"));
const AjioSellerOnboardingPillar = lazy(() => import("../pages/AjioSellerOnboardingPillar"));
const MyntraApprovalAgencies = lazy(() => import("../pages/MyntraApprovalAgencies"));
const NykaaOnboardingAgency = lazy(() => import("../pages/NykaaOnboardingAgency"));
const AjioOnboardingAgency = lazy(() => import("../pages/AjioOnboardingAgency"));

// ─── Kids Category Pages ──────────────────────────────────────────────
const KidsBrandMarketplaceOnboardingIndia = lazy(() => import("../pages/KidsBrandMarketplaceOnboardingIndia"));
const MyntraKidsBrandOnboarding = lazy(() => import("../pages/MyntraKidsBrandOnboarding"));
const FirstCryBrandOnboarding = lazy(() => import("../pages/FirstCryBrandOnboarding"));
const PillarPage = lazy(() => import("../pages/PillarPage"));
const KidsBrandOnboardingMumbai = lazy(() => import("../pages/KidsBrandOnboardingMumbai"));
const KidsBrandOnboardingBangalore = lazy(() => import("../pages/KidsBrandOnboardingBangalore"));
const KidsBrandOnboardingDelhi = lazy(() => import("../pages/KidsBrandOnboardingDelhi"));
const FirstCryOnboardingMumbai = lazy(() => import("../pages/FirstCryOnboardingMumbai"));
const FirstCryOnboardingBangalore = lazy(() => import("../pages/FirstCryOnboardingBangalore"));
const FirstCryOnboardingPune = lazy(() => import("../pages/FirstCryOnboardingPune"));

// ─── Programmatic City × Marketplace SEO Pages ────────────────────────
const NykaaSellerOnboardingIndia = lazy(() => import("../pages/NykaaSellerOnboardingCompanyIndia"));
const AjioSellerOnboardingIndia = lazy(() => import("../pages/AjioSellerOnboardingIndia"));
const MyntraOnboardingDelhi = lazy(() => import("../pages/MyntraOnboardingDelhi"));
const NykaaSellerOnboardingDelhi = lazy(() => import("../pages/NykaaSellerOnboardingDelhi"));
const AjioSellerOnboardingDelhi = lazy(() => import("../pages/AjioSellerOnboardingDelhi"));
const MyntraOnboardingMumbai = lazy(() => import("../pages/MyntraOnboardingMumbai"));
const NykaaSellerOnboardingMumbai = lazy(() => import("../pages/NykaaSellerOnboardingMumbai"));
const AjioSellerOnboardingMumbai = lazy(() => import("../pages/AjioSellerOnboardingMumbai"));
const MyntraOnboardingBangalore = lazy(() => import("../pages/MyntraOnboardingBangalore"));
const NykaaSellerOnboardingBangalore = lazy(() => import("../pages/NykaaSellerOnboardingBangalore"));
const AjioSellerOnboardingBangalore = lazy(() => import("../pages/AjioSellerOnboardingBangalore"));
const MyntraOnboardingHyderabad = lazy(() => import("../pages/MyntraOnboardingHyderabad"));
const NykaaSellerOnboardingHyderabad = lazy(() => import("../pages/NykaaSellerOnboardingHyderabad"));
const AjioSellerOnboardingHyderabad = lazy(() => import("../pages/AjioSellerOnboardingHyderabad"));
const MyntraOnboardingPune = lazy(() => import("../pages/MyntraOnboardingPune"));
const NykaaSellerOnboardingPune = lazy(() => import("../pages/NykaaSellerOnboardingPune"));
const AjioSellerOnboardingPune = lazy(() => import("../pages/AjioSellerOnboardingPune"));
const MyntraOnboardingChennai = lazy(() => import("../pages/MyntraOnboardingChennai"));
const NykaaSellerOnboardingChennai = lazy(() => import("../pages/NykaaSellerOnboardingChennai"));
const AjioSellerOnboardingChennai = lazy(() => import("../pages/AjioSellerOnboardingChennai"));
const MyntraOnboardingKolkata = lazy(() => import("../pages/MyntraOnboardingKolkata"));
const NykaaSellerOnboardingKolkata = lazy(() => import("../pages/NykaaSellerOnboardingKolkata"));
const AjioSellerOnboardingKolkata = lazy(() => import("../pages/AjioSellerOnboardingKolkata"));

// ─── Amazon Track (city × marketplace) ────────────────────────────────
const AmazonSellerOnboardingIndia = lazy(() => import("../pages/AmazonSellerOnboardingIndia"));
const AmazonSellerOnboardingDelhi = lazy(() => import("../pages/AmazonSellerOnboardingDelhi"));
const AmazonSellerOnboardingMumbai = lazy(() => import("../pages/AmazonSellerOnboardingMumbai"));
const AmazonSellerOnboardingBangalore = lazy(() => import("../pages/AmazonSellerOnboardingBangalore"));
const AmazonSellerOnboardingHyderabad = lazy(() => import("../pages/AmazonSellerOnboardingHyderabad"));
const AmazonSellerOnboardingPune = lazy(() => import("../pages/AmazonSellerOnboardingPune"));
const AmazonSellerOnboardingChennai = lazy(() => import("../pages/AmazonSellerOnboardingChennai"));
const AmazonSellerOnboardingKolkata = lazy(() => import("../pages/AmazonSellerOnboardingKolkata"));

// ─── Resources / New marketplaces / Pet track ─────────────────────────
const ResourcesPage = lazy(() => import("../pages/ResourcesPage"));
const TataCliqLuxuryOnboarding = lazy(() => import("../pages/TataCliqLuxuryOnboarding"));
const MirrawSellerOnboarding = lazy(() => import("../pages/MirrawSellerOnboarding"));
const ZalandoSellerOnboarding = lazy(() => import("../pages/ZalandoSellerOnboarding"));
const NoonSellerOnboarding = lazy(() => import("../pages/NoonSellerOnboarding"));
const PetMarketplaceOnboardingIndia = lazy(() => import("../pages/PetMarketplaceOnboardingIndia"));
const SupertailsSellerOnboarding = lazy(() => import("../pages/SupertailsSellerOnboarding"));
const HeadsUpForTailsSellerOnboarding = lazy(() => import("../pages/HeadsUpForTailsSellerOnboarding"));
const JustDogsSellerOnboarding = lazy(() => import("../pages/JustDogsSellerOnboarding"));

// ─── Global Marketplace Guides ────────────────────────────────────────
const GlobalMarketplaceSellerGuides = lazy(() => import("../pages/marketplace-guides/GlobalMarketplaceSellerGuides"));
const SellOnIndiaMarketplaces = lazy(() => import("../pages/marketplace-guides/SellOnIndiaMarketplaces"));
const SellOnUSMarketplaces = lazy(() => import("../pages/marketplace-guides/SellOnUSMarketplaces"));
const SellOnUAEMarketplaces = lazy(() => import("../pages/marketplace-guides/SellOnUAEMarketplaces"));
const NoonSellerOnboardingUAE = lazy(() => import("../pages/marketplace-guides/NoonSellerOnboardingUAE"));
const NamshiSellerOnboardingUAE = lazy(() => import("../pages/marketplace-guides/NamshiSellerOnboardingUAE"));
const AmazonUAESellerOnboarding = lazy(() => import("../pages/marketplace-guides/AmazonUAESellerOnboarding"));
const AmazonIndiaSellerOnboarding = lazy(() => import("../pages/marketplace-guides/AmazonIndiaSellerOnboarding"));
const AmazonUSSellerOnboarding = lazy(() => import("../pages/marketplace-guides/AmazonUSSellerOnboarding"));
const WalmartUSSellerOnboarding = lazy(() => import("../pages/marketplace-guides/WalmartUSSellerOnboarding"));
const MarketplaceSellerGuides = lazy(() => import("../pages/marketplace-guides/MarketplaceSellerGuides"));
const FarfetchSellerOnboarding = lazy(() => import("../pages/marketplace-guides/FarfetchSellerOnboarding"));
const OunassSellerOnboarding = lazy(() => import("../pages/marketplace-guides/OunassSellerOnboarding"));
const TrendyolSellerOnboarding = lazy(() => import("../pages/marketplace-guides/TrendyolSellerOnboarding"));
const EtsySellerOnboarding = lazy(() => import("../pages/marketplace-guides/EtsySellerOnboarding"));
const EbaySellerOnboarding = lazy(() => import("../pages/marketplace-guides/EbaySellerOnboarding"));
const NoonVsAmazonUAE = lazy(() => import("../pages/marketplace-guides/comparisons/NoonVsAmazonUAE"));
const AmazonVsWalmart = lazy(() => import("../pages/marketplace-guides/comparisons/AmazonVsWalmart"));
const MyntraVsAjio = lazy(() => import("../pages/marketplace-guides/comparisons/MyntraVsAjio"));
const NamshiVsNoonFashion = lazy(() => import("../pages/marketplace-guides/comparisons/NamshiVsNoonFashion"));

// ─── Blog detail pages (whitelisted to bypass dynamic /blog/:slug) ────
const MyntraSellersFail90DaysBlog = lazy(() => import("../pages/blog/MyntraSellersFail90DaysBlog"));
const MyntraVsAmazonComparisonBlog = lazy(() => import("../pages/blog/MyntraVsAmazonComparisonBlog"));

/**
 * Order preserved from App.js. Each entry: { path, Component }.
 * Rendered via .map() inside a single <Routes> block in App.js.
 */
export const LEAF_PAGE_ROUTES = [
  // Sprint 1 Myntra Pillar + Spokes
  // NOTE: /myntra-seller-account-management overridden by Sprint 4 flagship rebuild (App.js)
  { path: '/marketplace-account-management-services',           Component: MarketplaceAccountManagementHubPage },
  { path: '/amazon-seller-account-management',                  Component: AmazonAccountManagementGuide },
  { path: '/myntra-cataloging-services',                        Component: MyntraCatalogingServicesPage },
  { path: '/myntra-product-photoshoot-services',                Component: MyntraPhotoshootServicesPage },
  { path: '/myntra-pla-advertising-services',                   Component: MyntraPLAAdvertisingServicesPage },
  { path: '/myntra-return-management-services',                 Component: MyntraReturnManagementServicesPage },
  { path: '/myntra-payment-reconciliation-services',            Component: MyntraPaymentReconciliationServicesPage },
  { path: '/myntra-listing-optimization-services',              Component: MyntraListingOptimizationServicesPage },
  { path: '/myntra-sales-growth-services',                      Component: MyntraSalesGrowthServicesPage },

  // Onboarding Guides
  { path: '/nykaa-seller-onboarding-guide',                     Component: NykaaSellerOnboardingPage },
  { path: '/ajio-seller-onboarding-guide',                      Component: AjioSellerOnboardingPage },
  { path: '/tira-seller-onboarding-guide',                      Component: TiraSellerOnboardingPage },
  { path: '/pernias-pop-up-shop-seller-guide',                  Component: PerniasPopUpShopPage },

  // Misc landing / founder / short-URL
  { path: '/jinistudio',                                        Component: JiniStudioPage },
  { path: '/deepinder-ahluwalia',                               Component: DeepinderAhluwaliaPage },
  { path: '/myntra-onboarding-company-india',                   Component: MyntraOnboardingCompanyIndia },
  { path: '/myntra',                                            Component: MyntraLandingPage },
  { path: '/nykaa',                                             Component: NykaaLandingPage },
  { path: '/ajio',                                              Component: AjioLandingPage },
  { path: '/premium-marketplace-access-india-report-2026',      Component: PremiumMarketplaceAccessReport },
  { path: '/pet-marketplace-onboarding',                        Component: PetMarketplaceOnboarding },

  // LLM/GEO Pillars + Agencies
  { path: '/myntra-seller-onboarding',                          Component: MyntraSellerOnboardingPillar },
  { path: '/nykaa-seller-onboarding',                           Component: NykaaSellerOnboardingPillar },
  { path: '/ajio-seller-onboarding',                            Component: AjioSellerOnboardingPillar },
  { path: '/myntra-approval-agencies',                          Component: MyntraApprovalAgencies },
  { path: '/nykaa-onboarding-agency',                           Component: NykaaOnboardingAgency },
  { path: '/ajio-onboarding-agency',                            Component: AjioOnboardingAgency },

  // Kids Category
  { path: '/kids-brand-marketplace-onboarding-india',           Component: KidsBrandMarketplaceOnboardingIndia },
  { path: '/myntra-kids-brand-onboarding',                      Component: MyntraKidsBrandOnboarding },
  { path: '/firstcry',                                          Component: FirstCryBrandOnboarding },

  // Phase-B Pillar Authority Pages (PillarPage component, shared)
  { path: '/mirraw-seller-registration',                        Component: PillarPage },
  { path: '/tata-cliq-luxury-seller-onboarding',                Component: PillarPage },
  { path: '/zepto-seller-registration-india',                   Component: PillarPage },
  { path: '/nykaa-seller-onboarding-india',                     Component: PillarPage },
  // NOTE: /myntra-brand-approval-india overridden by Sprint 4 flagship rebuild (App.js)

  // Kids × City
  { path: '/kids-brand-onboarding-mumbai',                      Component: KidsBrandOnboardingMumbai },
  { path: '/kids-brand-onboarding-bangalore',                   Component: KidsBrandOnboardingBangalore },
  { path: '/kids-brand-onboarding-delhi',                       Component: KidsBrandOnboardingDelhi },
  { path: '/firstcry-onboarding-mumbai',                        Component: FirstCryOnboardingMumbai },
  { path: '/firstcry-onboarding-bangalore',                     Component: FirstCryOnboardingBangalore },
  { path: '/firstcry-onboarding-pune',                          Component: FirstCryOnboardingPune },

  // Programmatic SEO — India
  { path: '/ajio-seller-onboarding-india',                      Component: AjioSellerOnboardingIndia },

  // Programmatic SEO — Delhi
  { path: '/myntra-onboarding-company-delhi',                   Component: MyntraOnboardingDelhi },
  { path: '/nykaa-seller-onboarding-delhi',                     Component: NykaaSellerOnboardingDelhi },
  { path: '/ajio-seller-onboarding-delhi',                      Component: AjioSellerOnboardingDelhi },

  // Programmatic SEO — Mumbai
  { path: '/myntra-onboarding-company-mumbai',                  Component: MyntraOnboardingMumbai },
  { path: '/nykaa-seller-onboarding-mumbai',                    Component: NykaaSellerOnboardingMumbai },
  { path: '/ajio-seller-onboarding-mumbai',                     Component: AjioSellerOnboardingMumbai },

  // Programmatic SEO — Bangalore
  { path: '/myntra-onboarding-company-bangalore',               Component: MyntraOnboardingBangalore },
  { path: '/nykaa-seller-onboarding-bangalore',                 Component: NykaaSellerOnboardingBangalore },
  { path: '/ajio-seller-onboarding-bangalore',                  Component: AjioSellerOnboardingBangalore },

  // Programmatic SEO — Hyderabad
  { path: '/myntra-onboarding-company-hyderabad',               Component: MyntraOnboardingHyderabad },
  { path: '/nykaa-seller-onboarding-hyderabad',                 Component: NykaaSellerOnboardingHyderabad },
  { path: '/ajio-seller-onboarding-hyderabad',                  Component: AjioSellerOnboardingHyderabad },

  // Programmatic SEO — Pune
  { path: '/myntra-onboarding-company-pune',                    Component: MyntraOnboardingPune },
  { path: '/nykaa-seller-onboarding-pune',                      Component: NykaaSellerOnboardingPune },
  { path: '/ajio-seller-onboarding-pune',                       Component: AjioSellerOnboardingPune },

  // Programmatic SEO — Chennai
  { path: '/myntra-onboarding-company-chennai',                 Component: MyntraOnboardingChennai },
  { path: '/nykaa-seller-onboarding-chennai',                   Component: NykaaSellerOnboardingChennai },
  { path: '/ajio-seller-onboarding-chennai',                    Component: AjioSellerOnboardingChennai },

  // Programmatic SEO — Kolkata
  { path: '/myntra-onboarding-company-kolkata',                 Component: MyntraOnboardingKolkata },
  { path: '/nykaa-seller-onboarding-kolkata',                   Component: NykaaSellerOnboardingKolkata },
  { path: '/ajio-seller-onboarding-kolkata',                    Component: AjioSellerOnboardingKolkata },

  // Resources Hub + New marketplaces
  { path: '/resources',                                         Component: ResourcesPage },
  { path: '/tata-cliq-luxury-onboarding',                       Component: TataCliqLuxuryOnboarding },
  { path: '/mirraw-seller-onboarding',                          Component: MirrawSellerOnboarding },
  { path: '/zalando-seller-onboarding',                         Component: ZalandoSellerOnboarding },
  { path: '/noon-seller-onboarding',                            Component: NoonSellerOnboarding },

  // Amazon Track (city × marketplace)
  { path: '/amazon-seller-onboarding-india',                    Component: AmazonSellerOnboardingIndia },
  { path: '/amazon-seller-onboarding-delhi',                    Component: AmazonSellerOnboardingDelhi },
  { path: '/amazon-seller-onboarding-mumbai',                   Component: AmazonSellerOnboardingMumbai },
  { path: '/amazon-seller-onboarding-bangalore',                Component: AmazonSellerOnboardingBangalore },
  { path: '/amazon-seller-onboarding-hyderabad',                Component: AmazonSellerOnboardingHyderabad },
  { path: '/amazon-seller-onboarding-pune',                     Component: AmazonSellerOnboardingPune },
  { path: '/amazon-seller-onboarding-chennai',                  Component: AmazonSellerOnboardingChennai },
  { path: '/amazon-seller-onboarding-kolkata',                  Component: AmazonSellerOnboardingKolkata },

  // Pet Marketplace Track
  { path: '/pet-marketplace-onboarding-india',                  Component: PetMarketplaceOnboardingIndia },
  { path: '/supertails-seller-onboarding',                      Component: SupertailsSellerOnboarding },
  { path: '/heads-up-for-tails-seller-onboarding',              Component: HeadsUpForTailsSellerOnboarding },
  { path: '/justdogs-seller-onboarding',                        Component: JustDogsSellerOnboarding },

  // 301-style soft redirect (same Component as canonical India page)
  { path: '/nykaa-seller-onboarding-company-india',             Component: NykaaSellerOnboardingIndia },

  // Global Marketplace SEO Pages
  { path: '/global-marketplace-seller-guides',                  Component: GlobalMarketplaceSellerGuides },
  { path: '/sell-on-india-marketplaces',                        Component: SellOnIndiaMarketplaces },
  { path: '/sell-on-us-marketplaces',                           Component: SellOnUSMarketplaces },
  { path: '/sell-on-uae-marketplaces',                          Component: SellOnUAEMarketplaces },
  { path: '/noon-seller-onboarding-uae',                        Component: NoonSellerOnboardingUAE },
  { path: '/namshi-seller-onboarding-uae',                      Component: NamshiSellerOnboardingUAE },
  { path: '/amazon-uae-seller-onboarding',                      Component: AmazonUAESellerOnboarding },
  { path: '/amazon-india-seller-onboarding',                    Component: AmazonIndiaSellerOnboarding },
  { path: '/amazon-us-seller-onboarding',                       Component: AmazonUSSellerOnboarding },
  { path: '/walmart-us-seller-onboarding',                      Component: WalmartUSSellerOnboarding },

  // Additional Marketplace Guides
  { path: '/marketplace-seller-guides',                         Component: MarketplaceSellerGuides },
  { path: '/farfetch-seller-onboarding',                        Component: FarfetchSellerOnboarding },
  { path: '/ounass-seller-onboarding',                          Component: OunassSellerOnboarding },
  { path: '/trendyol-seller-onboarding',                        Component: TrendyolSellerOnboarding },
  { path: '/etsy-seller-onboarding',                            Component: EtsySellerOnboarding },
  { path: '/ebay-seller-onboarding',                            Component: EbaySellerOnboarding },

  // Marketplace Comparison Pages
  { path: '/noon-vs-amazon-uae-sellers',                        Component: NoonVsAmazonUAE },
  { path: '/amazon-vs-walmart-marketplace',                     Component: AmazonVsWalmart },
  { path: '/myntra-vs-ajio-seller-platform',                    Component: MyntraVsAjio },
  { path: '/namshi-vs-noon-fashion-marketplace',                Component: NamshiVsNoonFashion },

  // Myntra Blog Series (whitelisted before /blog/:slug)
  { path: '/blog/why-myntra-sellers-fail-first-90-days',        Component: MyntraSellersFail90DaysBlog },
  { path: '/blog/myntra-vs-amazon-fashion-brands-india',        Component: MyntraVsAmazonComparisonBlog },
];
